import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  TextField,
  IconButton,
  Button,
  makeStyles,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import axios from "axios";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";
import StyledButton from "../../../common/models/Button";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(2),
  },
  table: {
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    width: "900px",
  },
  headerCell: {
    fontWeight: 600,
    color: "#333",
    backgroundColor: "#f0f0f0",
    padding: "10px 16px",
    textAlign: "left",
  },
  bodyCell: {
    padding: "10px 16px",
    textAlign: "left",
    verticalAlign: "middle",
  },
  textField: {
    "& input": {
      padding: "6px 8px",
      height: "30px",
    },
  },
  addButton: {
    marginTop: theme.spacing(2),
    backgroundColor: "#fff",
    color: "#000",
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    fontWeight: 600,
    border: "1px solid #000",
    display: "flex",
    alignItems: "center",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  addIconCircle: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    border: "2px solid #000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1),
    fontSize: "16px",
    fontWeight: 600,
  },
  deleteIcon: {
    color: theme.palette.error.main,
  },
  switchBase: {
    "&.Mui-checked": {
      color: "#007bff",
    },
    "&.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#007bff",
    },
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    marginTop: theme.spacing(4),
  },
  iconBtn: {
    padding: "4px",
  },
  inlineActions: {
    display: "flex",
    flexDirection: "column",
    marginLeft: 8,
  },
  backButton: {
    backgroundColor: "#555",
    color: "#fff",
    borderRadius: "20px",
    padding: "8px 24px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#333",
    },
  },
  resetChangesButton: {
    border: "1px solid #000000",
    color: "#000000",
    fontWeight: 600,
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    marginTop: theme.spacing(2),
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  saveButton: {
    backgroundColor: "#f26522",
    color: "#fff",
    borderRadius: "20px",
    padding: "8px 24px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#d4551a",
    },
  },
}));

const MaterialTypeTab = (props) => {
  const classes = useStyles();

  // Accept props for navigation and context
    const { addSponsorList, rowData, updateNetworkData, rowNetwork, handleNext, handleBack, setMaterialType } = props;

  const [materialTypes, setMaterialTypes] = useState([]);
  const [isUpdated, setIsUpdated] = useState(false);
  const [savingStates, setSavingStates] = useState({});
  const [isSaving, setIsSaving] = useState(false); // Track save & next button loading
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const network = addSponsorList?.data?.network || rowNetwork;
  const networkUrl = updateNetworkData?.ecosystemUrl || rowData?.networkURL;
  const ecosystemId = addSponsorList?.data?.ecosystemId || rowData?.ecosystemId;
    const sponsorId = addSponsorList?.data?.organizationId || rowData?.organizationId;

  const fetchMaterialTypes = async () => {
    setError(null);
    setLoading(true);
        const cmoConfig = false
    try {
      const url = `${networkUrl}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/fetchMaterialTypes/${cmoConfig}`;
      const response = await axios.get(url);
      const data = response?.data;
      if (data?.status === true || data?.status === "true") {
        const list = data.materialTypeList || [];
        const formatted = list.map((item, index) => ({
          id: item.materialTypeID || `ID-${index + 1}`,
          name: item.materialTypeName || "",
          type: item.materialType,
          selected: !!item.activated,
          isNew: false,
        }));
        setMaterialTypes(formatted);
        if (props.setMaterialType) props.setMaterialType((prev) => !prev);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchMaterialTypes();
  }, []);

  const saveNewMaterialType = async (materialType) => {
    try {
      const payload = {
        ecosystemId,
        sponsorId,
        materialTypeList: [
          {
            materialTypeName: materialType.name,
            materialType: "New",
            activated: materialType.selected,
          },
        ],
      };
      const url = `${networkUrl}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/addMaterialTypes`;
      const response = await axios.post(url, payload);
      if (setMaterialType) setMaterialType((prev) => !prev);
      return response.data?.status === true || response.data?.status === "true";
    } catch (error) {
      return false;
    }
  };

  const handleSaveNewMaterialType = async (index) => {
    const materialType = materialTypes[index];
    if (!materialType.name?.trim() || savingStates[index]) return;
    setSavingStates((prev) => ({ ...prev, [index]: true }));
    const success = await saveNewMaterialType(materialType);
    if (success) {
      await fetchMaterialTypes();
    } else {
      setErrorMessage("Failed to save new material type.");
    }
    setSavingStates((prev) => ({ ...prev, [index]: false }));
  };

  const handleSaveChanges = async () => {
    setIsSaving(true); // Start loading
    try {
            const isAnyMaterialTypeSelected = materialTypes.filter((materialType) => materialType.isUpdated);
      if (!isAnyMaterialTypeSelected.length) {
        if (handleNext) handleNext();
        return;
      }
      if (materialTypes.every((materialType) => !materialType.selected)) {
        setErrorMessage("Please enable at least one in toggle mode.");
        return;
      }
      const payload = {
        ecosystemId,
        sponsorId,
                materialTypeList: isAnyMaterialTypeSelected.map(({ id, name, selected }) => ({
            materialTypeID: id,
            materialTypeName: name,
            activated: selected,
            markForDelete: false,
                })),
      };
      const url = `${networkUrl}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/updateMaterialTypes`;
      const response = await axios.post(url, payload);
      if (response.data?.status === true || response.data?.status === "true") {
        setIsUpdated(false);
        await fetchMaterialTypes();
        setErrorMessage("");
        if (handleNext) handleNext();
      } else {
        setErrorMessage("Failed to update Material Types. Please try again.");
      }
    } catch (error) {
            setErrorMessage("An error occurred while updating Material Types. Please try again.");
    } finally {
      setIsSaving(false); // Stop loading
    }
  };

  const deleteMaterialType = async (materialType) => {
    try {
      if (materialType.isNew) {
                setMaterialTypes((prevMaterialTypes) => prevMaterialTypes.filter((item) => item.id !== materialType.id));
      } else {
        const url = `${networkUrl}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/updateMaterialTypes`;
        const payload = {
          ecosystemId,
          sponsorId,
          materialTypeList: [
            {
              materialTypeID: materialType.id,
              materialTypeName: materialType.name,
              activated: materialType.selected,
              markForDelete: true,
            },
          ],
        };
        const response = await axios.post(url, payload);
                if (response.data?.status === true || response.data?.status === "true") {
          await fetchMaterialTypes();
          if (setMaterialType) setMaterialType((prev) => !prev);
        } else {
          setErrorMessage("Failed to delete Material Type.");
        }
      }
    } catch (error) {
      setErrorMessage("Error deleting Material Type.");
    }
  };

  const handleResetChanges = () => {
    if (isUpdated) {
      fetchMaterialTypes();
      setIsUpdated(false);
      setErrorMessage("");
    }
  };
  const handleToggle = (index) => {
    const updated = [...materialTypes];
    updated[index].selected = !updated[index].selected;
    updated[index].isUpdated = true;
    setMaterialTypes(updated);
    setIsUpdated(true);
  };

  const handleNameChange = (index, value) => {
    const updated = [...materialTypes];
    updated[index].name = value;
    setMaterialTypes(updated);
    setIsUpdated(true);
  };

  const handleAddMaterialType = () => {
    setMaterialTypes([
      ...materialTypes,
      {
        name: "",
        selected: false,
        isNew: true,
      },
    ]);
    setIsUpdated(true);
  };
  const handleCancel = (index) => {
    const updatedMaterialTypes = materialTypes.filter((_, i) => i !== index);
    setMaterialTypes(updatedMaterialTypes);
    setIsUpdated(true);
  };

  return (
    <div className={classes.container}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Selection</TableCell>
            <TableCell className={classes.headerCell}>Material Type</TableCell>
                        <TableCell className={classes.headerCell} style={{ whiteSpace: 'nowrap' }}>
              Material Type ID
            </TableCell>
            <TableCell className={classes.headerCell}>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={4}>
                                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100px" }}>
                  <CircularProgress />
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={4}>
                                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60px", backgroundColor: "#ffecec", color: "#b00020", border: "1px solid #ff9999" }}>
                  <Typography>{error}</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            materialTypes.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell className={classes.bodyCell}>
                  <Switch
                    checked={item.selected}
                    onChange={() => handleToggle(index)}
                    classes={{ switchBase: classes.switchBase }}
                  />
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  {item.isNew ? (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <TextField
                        placeholder="Type Text Here"
                        value={item.name}
                        onChange={(e) =>
                          handleNameChange(index, e.target.value)
                        }
                        onBlur={(e) => {
                          const trimmed = e.target.value.trim();
                          if (trimmed !== e.target.value) {
                            handleNameChange(index, trimmed);
                          }
                        }}
                        className={classes.textField}
                        variant="outlined"
                        inputProps={{
                          onBeforeInput: (e) => {
                            const allowedChars = /^[a-zA-Z0-9\s()/\-.]+$/;
                            if (!allowedChars.test(e.data)) {
                              e.preventDefault();
                            }
                                                    }
                        }}
                      />
                      <div className={classes.inlineActions}>
                                                <IconButton onClick={() => handleCancel(index)} className={classes.iconBtn}>
                          <CancelIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => handleSaveNewMaterialType(index)}
                          className={classes.iconBtn}
                          disabled={
                            !materialTypes[index].name?.trim() ||
                                                        materialTypes.some((m, i) => i !== index && m.name?.trim().toLowerCase() === materialTypes[index].name?.trim().toLowerCase()) ||
                            savingStates[index]
                          }
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                  ) : (
                    item.name
                  )}
                </TableCell>
                <TableCell className={classes.bodyCell}>{item.id}</TableCell>
                <TableCell className={classes.bodyCell}>
                  {(item.isNew === true || item.type === "New") && (
                                        <IconButton aria-label="delete" onClick={() => deleteMaterialType(item)}>
                      <DeleteIcon className={classes.deleteIcon} />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {errorMessage && (
        <div style={{ color: "red", marginTop: 10 }} className="error-message">
          {errorMessage}
        </div>
      )}
            <Button variant="outlined" onClick={handleAddMaterialType} className={classes.addButton}>
        <span className={classes.addIconCircle}>+</span>
        Add New Material Type
      </Button>
      <div className={classes.footer}>
                <Button className={classes.backButton} onClick={() => { if (handleBack) handleBack(); }}>
          &lt; Back
        </Button>
        <StyledButton
          className={classes.saveButton}
          onClick={() => {
            if (isUpdated) {
              handleSaveChanges();
            } else if (handleNext) {
              handleNext();
            }
          }}
          style={{
            opacity: isSaving ? 0.7 : 1,
                        pointerEvents: isSaving ? 'none' : 'auto' 
          }}
        >
          {isSaving ? (
            <>
                            <CircularProgress size={16} color="inherit" style={{ marginRight: '8px' }} />
              Saving...
            </>
          ) : (
                        'Save & Next'
          )}
        </StyledButton>
      </div>
    </div>
  );
};

export default MaterialTypeTab;
